import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  Modal,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Delete,
} from "@mui/icons-material";
import { investorApi, vendorApi } from "../../../api/api";
 
const YourIndividualVillaImages = ({ propertyId }) => {
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [hoveredImage, setHoveredImage] = useState(null);
  const theme = useTheme();
 
  useEffect(() => {
    if (!propertyId) return;
    const fetchImages = async () => {
      try {
        const response = await investorApi.getImagesByPropertyId(propertyId);
        setImages(response.data);
      } catch (error) {
        console.error("Failed to fetch images:", error);
      }
    };
    fetchImages();
  }, [propertyId]);
 
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
 
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };
 
  const handleOpenModal = (index) => {
    setCurrentImageIndex(index);
    setOpenModal(true);
  };
 
  const handleCloseModal = () => setOpenModal(false);
 
  const handleDeleteImage = async (imageId) => {
    const confirmed = window.confirm("Are you sure you want to delete this image?");
    if (!confirmed) return;
 
    try {
      await vendorApi.deleteImageById({ id: imageId, type: 4 });
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      alert("Image deleted successfully.");
    } catch (error) {
      alert("Failed to delete image. Please try again.");
    }
  };
 
  if (images.length === 0) return null;
 
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: theme.shadows[3],
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row", // always row (even on mobile)
          gap: 1,
          height: { xs: "70vh", sm: "80vh" },
        }}
      >
        {/* Left side: single big image */}
        <Box
          sx={{
            flex: 1,
            height: "100%",
            position: "relative",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {images[0] && (
            <Card
              sx={{
                height: "100%",
                transition: "all 0.3s ease-in-out",
                transform: hoveredImage === 0 ? "scale(1.02)" : "scale(1)",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: theme.shadows[8],
                }
              }}
              onMouseEnter={() => setHoveredImage(0)}
              onMouseLeave={() => setHoveredImage(null)}
            >
              <CardMedia
                component="img"
                image={images[0].ImageURL}
                alt="Main Image"
                sx={{
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                  transition: "all 0.3s ease-in-out",
                  filter: hoveredImage === 0 ? "brightness(1.1)" : "brightness(1)",
                }}
                onClick={() => handleOpenModal(0)}
              />
              {/* Delete icon for main image */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteImage(images[0].id);
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 0, 0, 0.7)",
                  color: "white",
                  transition: "all 0.3s ease-in-out",
                  transform: hoveredImage === 0 ? "scale(1.1)" : "scale(1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 0, 0, 0.9)",
                    transform: "scale(1.2)",
                  },
                }}
              >
                <Delete />
              </IconButton>
              {/* Hover overlay */}
              {/* <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  opacity: hoveredImage === 0 ? 1 : 0,
                  transition: "opacity 0.3s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    color: "white",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  }}
                >
                  Click to view
                </Box>
              </Box> */}
            </Card>
          )}
        </Box>
 
        {/* Right side: two stacked images */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
          }}
        >
          {images.slice(1, 3).map((img, i) => {
            const imageIndex = i + 1;
            return (
              <Box
                key={img.id}
                sx={{
                  flex: 1,
                  position: "relative",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease-in-out",
                    transform: hoveredImage === imageIndex ? "scale(1.02)" : "scale(1)",
                    cursor: "pointer",
                    // "&:hover": {
                    //   boxShadow: theme.shadows[0],
                    // }
                  }}
                  onMouseEnter={() => setHoveredImage(imageIndex)}
                  onMouseLeave={() => setHoveredImage(null)}
                >
                  <CardMedia
                    component="img"
                    image={img.ImageURL}
                    alt={`Image ${i + 2}`}
                    sx={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      filter: hoveredImage === imageIndex ? "brightness(1.1)" : "brightness(1)",
                    }}
                    onClick={() => handleOpenModal(imageIndex)}
                  />
                  {/* Delete icon for right side images */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(img.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(255, 0, 0, 0.7)",
                      color: "white",
                      transition: "all 0.3s ease-in-out",
                      transform: hoveredImage === imageIndex ? "scale(1.1)" : "scale(1)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.9)",
                        transform: "scale(1.2)",
                      },
                    }}
                  >
                    <Delete />
                  </IconButton>
                  {/* Hover overlay */}
                  {/* <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      opacity: hoveredImage === imageIndex ? 1 : 0,
                      transition: "opacity 0.3s ease-in-out",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      sx={{
                        color: "white",
                        fontSize: "1rem",
                        fontWeight: "bold",
                        textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                      }}
                    >
                      Click to view
                    </Box>
                  </Box> */}
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>
 
      {/* Fullscreen Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
        }}
      >
        <Box
          sx={{
            width: "95vw",
            height: "95vh",
            bgcolor: "background.paper",
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",
            boxShadow: theme.shadows[10],
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              zIndex: 1,
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.8)",
                transform: "scale(1.1)",
              },
            }}
          >
            <Close />
          </IconButton>
 
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {images[currentImageIndex] && (
                <>
                  <CardMedia
                    component="img"
                    image={images[currentImageIndex].ImageURL}
                    alt="Property Image"
                    sx={{
                      objectFit: "contain",
                      width: "100%",
                      height: "100%",
                      maxHeight: "calc(95vh - 80px)",
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                  />
 
                  <IconButton
                    onClick={() => {
                      handleDeleteImage(images[currentImageIndex].id);
                      handleCloseModal();
                    }}
                    sx={{
                      position: "absolute",
                      top: 16,
                      left: 16,
                      backgroundColor: "rgba(255, 0, 0, 0.6)",
                      color: "white",
                      zIndex: 1,
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.8)",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <Delete />
                  </IconButton>
                </>
              )}
 
              <IconButton
                onClick={prevImage}
                sx={{
                  position: "absolute",
                  left: 16,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "white",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.8)",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <ChevronLeft fontSize="large" />
              </IconButton>
              <IconButton
                onClick={nextImage}
                sx={{
                  position: "absolute",
                  right: 16,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "white",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.8)",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <ChevronRight fontSize="large" />
              </IconButton>
            </Box>
 
            <Box
              sx={{
                p: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`${currentImageIndex + 1} / ${images.length}`}
                  sx={{
                    backgroundColor: theme.palette.action.selected,
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                      transform: "scale(1.05)",
                    }
                  }}
                />
              </Stack>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
 
export default YourIndividualVillaImages;